"use client";

import { useEffect, useRef } from "react";

// Cloudflare Turnstile, rendered explicitly so it survives React remounts (the
// register form is re-keyed after every failed submit, and a Turnstile token is
// single-use, so each mount gets a fresh widget). The widget puts its token in
// a hidden `cf-turnstile-response` input inside `container`, i.e. inside the
// form, and lib/auth/bot-check.ts verifies it. The script origin is allowed in
// next.config.ts's CSP (script-src + frame-src).

const SCRIPT_SRC = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";

interface TurnstileApi {
  render(
    container: HTMLElement,
    options: {
      sitekey: string;
      action?: string;
      language?: string;
      theme?: "auto" | "light" | "dark";
      callback?: (token: string) => void;
      "expired-callback"?: () => void;
      "error-callback"?: () => void;
    },
  ): string;
  remove(widgetId: string): void;
}

declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

let scriptPromise: Promise<TurnstileApi> | null = null;

function loadTurnstile(): Promise<TurnstileApi> {
  if (window.turnstile) return Promise.resolve(window.turnstile);
  scriptPromise ??= new Promise<TurnstileApi>((resolve, reject) => {
    const script = document.createElement("script");
    script.src = SCRIPT_SRC;
    script.async = true;
    script.onload = () => (window.turnstile ? resolve(window.turnstile) : reject(new Error("turnstile missing")));
    script.onerror = () => {
      scriptPromise = null;
      script.remove();
      reject(new Error("turnstile script failed to load"));
    };
    document.head.appendChild(script);
  });
  return scriptPromise;
}

export function TurnstileWidget({
  siteKey,
  action,
  language,
  onSolvedChange,
  onLoadError,
}: {
  siteKey: string;
  action: string;
  language?: string;
  onSolvedChange: (solved: boolean) => void;
  onLoadError: () => void;
}) {
  const container = useRef<HTMLDivElement>(null);
  // Latest callbacks without re-rendering the widget when they change.
  const callbacks = useRef({ onSolvedChange, onLoadError });
  useEffect(() => {
    callbacks.current = { onSolvedChange, onLoadError };
  });

  useEffect(() => {
    let widgetId: string | null = null;
    let cancelled = false;
    loadTurnstile()
      .then((api) => {
        if (cancelled || !container.current) return;
        widgetId = api.render(container.current, {
          sitekey: siteKey,
          action,
          language,
          theme: "auto",
          callback: () => callbacks.current.onSolvedChange(true),
          "expired-callback": () => callbacks.current.onSolvedChange(false),
          "error-callback": () => callbacks.current.onSolvedChange(false),
        });
      })
      .catch(() => {
        if (!cancelled) callbacks.current.onLoadError();
      });
    return () => {
      cancelled = true;
      if (widgetId) window.turnstile?.remove(widgetId);
    };
  }, [siteKey, action, language]);

  return <div ref={container} className="min-h-[65px]" />;
}
